/**
 * fmtDate.ts — дата для человека в кабинете (WEB-3310 юнит 3а), свой модуль
 * (WEB-4000).
 *
 * Почему не в `format.ts`: тот нужен публичному входу воронки и лежит в общем
 * куске первого экрана покупателя (`global-*.js`), а сборщик оставляет в куске
 * каждую функцию, которую кто-то вызывает. Первый вызов `fmtDate` со страницы
 * кабинета перетаскивал его код (около 494 байт) в первый экран воронки. Здесь
 * его публичный вход не видит; сторож — `__tests__/fmtDate.publicReach.test.ts`.
 * Поэтому `fmtDate` не переэкспортируют ни `format.ts`, ни `@/i18n`: импорт
 * только `@/i18n/fmtDate`.
 */
import { dateTimeFormat } from './format';
import { getUiLocale, type UiLocale } from './locale';

/** Настройки `fmtDate`: какие части даты писать. */
export interface FmtDateOptions {
  /** `'auto'` — год текущего года не пишется. */
  year?: 'auto';
  /** Время «чч:мм». */
  time?: boolean;
  /** Секунды (вместе с `time`). */
  seconds?: boolean;
  /** Короткий месяц и в ru (оси графиков). */
  short?: boolean;
}

/**
 * ДАТА ДЛЯ ЧЕЛОВЕКА — месяц словом, порядок по языку (WEB-3310 юнит 3а, эталон
 * `design/cabinet-rescin/kit.jsx`, `fmtDate`). Владелец 20.10: «01.07.2026
 * формат даты мне не нравится. Если американцы пользуются — они с ума сойдут»,
 * «он должен быть обобщён»: ru «1 июля 2026», en «Jul 1, 2026»; цифрами
 * день.месяц американец читает наоборот (01.07 — 7 января).
 *
 * Строка `ГГГГ-ММ-ДД` читается как ПОЛДЕНЬ местного времени: голая дата по
 * стандарту — полночь UTC, и западнее Гринвича она съезжала бы на сутки назад.
 * Неразбираемое значение возвращается строкой как есть (как в эталоне; у
 * `formatDate` — пустая строка). Русское «г.» после года убирается — только
 * сразу после цифры года: короткий месяц «авг.» кончается тем же «г.» (WEB-4002).
 * Время в ru — `hourCycle: 'h23'` и две цифры часа (полночь — «00:05», правило
 * `formatDateTime`), в en — 12-часовое, как в эталоне.
 */
export function fmtDate(
  value: Date | string | number,
  opt: FmtDateOptions = {},
  locale: UiLocale = getUiLocale(),
): string {
  const date =
    value instanceof Date ? value : new Date(typeof value === 'string' && value.length === 10 ? value + 'T12:00:00' : value);
  if (Number.isNaN(date.getTime())) return String(value);
  const en = locale === 'en';
  const o: Intl.DateTimeFormatOptions = { day: 'numeric', month: en || opt.short ? 'short' : 'long' };
  if (!(opt.year === 'auto' && date.getFullYear() === new Date().getFullYear())) o.year = 'numeric';
  if (opt.time) {
    Object.assign(o, en ? { hour: 'numeric' } : { hour: '2-digit', hourCycle: 'h23' }, { minute: '2-digit' });
    if (opt.seconds) o.second = '2-digit';
  }
  return dateTimeFormat(o, locale).format(date).replace(/(\d)\s?г\./, '$1');
}
